import { ofType } from 'redux-observable';
import React from 'react';
import { EMPTY, of, concat } from 'rxjs';
import get from 'lodash.get';
import { catchError, mergeMap } from 'rxjs/operators';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { ModalService } from 'ui-components/services/modal-service';
import Global from '../../../store/global-selectors';
import { OrderSearchService } from '../../../services/order-search.service';
import { addFulfillmentPlansToBatch } from '../batches.api';
import { getOrdersForOrderNumber } from '../../orders/orders.api';
import ScanToBatchModal from '../../../components/batches/scan-to-batch/scan-to-batch-modal';
import * as Actions from './scan-to-batch.actions';
import { getBatchDetails } from '../details/batch.details.actions';
import { getBatchMenuData } from '../nav/batches.nav.actions';
import * as BatchDetailsApi from '../details/batch.details.api';
import { bulkLoadFulfillmentPlans$ } from '../../bulkload/bulkload.observables';

export const openScanToBatchModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_SCAN_TO_BATCH_MODAL),
    mergeMap(() => {
      ModalService.launchModal({
        component: ScanToBatchModal,
      });
      return EMPTY;
    }),
  );
};

const getFulfillmentPlansFromOrderNumber = (orderNumber) => {
  return getOrdersForOrderNumber(orderNumber).pipe(
    mergeMap((response) => {
      if (Array.isArray(response) && response.length) {
        const fulfillmentPlanIdsSet = new Set();
        response.forEach(({ fulfillmentPlans }) => {
          fulfillmentPlans.forEach(({ fulfillmentPlanId }) => fulfillmentPlanIdsSet.add(fulfillmentPlanId));
        });
        return of(Actions.addFulfillmentPlansToBatch([...fulfillmentPlanIdsSet]));
      }
      return of(Actions.orderNotFound(orderNumber));
    }),
  );
};

export const scanToBatchFulFillmentPlanSearch = (action$) => {
  return action$.pipe(
    ofType(Actions.SCAN_TO_BATCH_FULFILLMENT_PLAN_SEARCH),
    mergeMap((action) => {
      const getOrder = OrderSearchService.getOrderId(action.searchString);
      const parsedFulfillmentPlanId = parseInt(getOrder, 16);
      const fulfillmentPlanId = parsedFulfillmentPlanId ? `${parsedFulfillmentPlanId}` : '';
      if (!fulfillmentPlanId) {
        return getFulfillmentPlansFromOrderNumber(action.searchString);
      }
      return bulkLoadFulfillmentPlans$([fulfillmentPlanId]).pipe(
        mergeMap(() => of(Actions.addFulfillmentPlansToBatch([fulfillmentPlanId]))),
        catchError(() => of(Actions.orderNotFound(fulfillmentPlanId))),
      );
    }),
  );
};

export const addFulfillmentPlansToBatchFromScan = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ADD_FULFILLMENT_PLANS_TO_BATCH),
    mergeMap((action) => {
      const { fulfillmentPlanIds } = action;
      const state = getState();
      const currentBatch = Global.selectBatchDetailsBatch(state);
      const { fulfillmentPlanSetId } = currentBatch;
      if (fulfillmentPlanSetId) {
        return addFulfillmentPlansToBatch(fulfillmentPlanSetId, fulfillmentPlanIds).pipe(
          mergeMap((response) => {
            const fulfillmentPlansInOtherBatches = get(response, 'data.fulfillmentPlansInOtherBatches');
            if (fulfillmentPlansInOtherBatches.length > 0) {
              return of(Actions.scannedOrderIsInAnotherBatch(fulfillmentPlanIds, fulfillmentPlanSetId));
            }
            return concat(of(getBatchMenuData()), of(getBatchDetails(fulfillmentPlanSetId)));
          }),
        );
      }
      return EMPTY;
    }),
  );
};

export const scannedOrderIsInAnotherBatch = (action$) => {
  return action$.pipe(
    ofType(Actions.SCANNED_ORDER_IS_IN_ANOTHER_BATCH),
    mergeMap(({ fulfillmentPlanIds, fulfillmentPlanSetId }) => {
      toast.info(
        <FormattedMessage
          defaultMessage='{fpIdsCount, plural, one {Order is} other {Orders are}} in another batch. Moving to current batch.'
          description='batch.details.forceAddingOrderContent'
          id='p7m084'
          values={{ fpIdsCount: fulfillmentPlanIds.length }}
        />,
      );
      return BatchDetailsApi.forceAddFulfillmentPlans(fulfillmentPlanSetId, fulfillmentPlanIds).pipe(
        mergeMap(() => concat(of(getBatchMenuData()), of(getBatchDetails(fulfillmentPlanSetId)))),
        catchError(() => of(Actions.unableToMoveOrderToBatch())),
      );
    }),
  );
};

export const unableToMoveOrderToBatch = (action$) => {
  return action$.pipe(
    ofType(Actions.UNABLE_TO_MOVE_ORDER_TO_BATCH),
    mergeMap(() => {
      toast.error(
        <FormattedMessage
          defaultMessage='The scanned order is currently in another batch and was not added.'
          description='batch.details.orderAnotherBatchContent'
          id='riebT+'
        />,
      );
      return EMPTY;
    }),
  );
};

export const scannedOrderWasNotFound = (action$) => {
  return action$.pipe(
    ofType(Actions.SCANNED_ORDER_IS_NOT_FOUND),
    mergeMap(() => {
      toast.info(<FormattedMessage defaultMessage='Order was not found' description='batch.details.orderNotFoundContent' id='ciheyQ' />);
      return EMPTY;
    }),
  );
};
